import { selectRouteLinkAnalyse, exportRouteLinkAnalyse } from '@/services/connectivityAnalysis';
import { message } from 'antd';

const buildMapData = (records = []) => {
  const pointMap = new Map();
  const connectionMap = new Map();

  const toNumber = (value) => {
    if (value === null || value === undefined || value === '') {
      return undefined;
    }
    const num = Number(value);
    return Number.isFinite(num) ? num : undefined;
  };

  const addPoint = (key, name, lng, lat, extra = {}) => {
    if (!key) {
      return;
    }
    const longitude = toNumber(lng);
    const latitude = toNumber(lat);
    if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
      return;
    }
    if (!pointMap.has(key)) {
      pointMap.set(key, {
        id: key,
        name: name || key,
        lng: longitude,
        lat: latitude,
        ...extra,
      });
    }
  };

  const normalizeDirection = (value) => {
    if (value === undefined || value === null) {
      return 'unidirectional';
    }
    const normalized = String(value).trim().toLowerCase();
    if (normalized.includes('双') || normalized.includes('双向')) {
      return 'bidirectional';
    }
    return 'unidirectional';
  };

  records.forEach((item) => {
    const startKey = item.startSiteCode || item.startSiteId;
    const arriveKey = item.arriveSiteCode || item.arriveSiteId;

    const startName = item.shortStartSiteName || item.startSiteName;
    const arriveName = item.shortArriveSiteName || item.arriveSiteName;

    addPoint(startKey, startName, item.startSiteLon, item.startSiteLat, {
      regionCode: item.startRegionCode,
      regionName: item.startRegionName,
      provinceCode: item.startProvinceCode,
      provinceName: item.startProvinceName,
    });
    addPoint(arriveKey, arriveName, item.arriveSiteLon, item.arriveSiteLat, {
      regionCode: item.arriveRegionCode,
      regionName: item.arriveRegionName,
      provinceCode: item.arriveProvinceCode,
      provinceName: item.arriveProvinceName,
    });

    if (!startKey || !arriveKey) {
      return;
    }

    const directionType = normalizeDirection(item.direction);
    const forwardKey = `${startKey}->${arriveKey}`;
    const reverseKey = `${arriveKey}->${startKey}`;

    if (directionType === 'bidirectional') {
      const undirectedKey =
        startKey < arriveKey ? `${startKey}|${arriveKey}` : `${arriveKey}|${startKey}`;
      if (!connectionMap.has(undirectedKey)) {
        connectionMap.set(undirectedKey, {
          id: undirectedKey,
          from: startKey,
          to: arriveKey,
          type: 'bidirectional',
          meta: item,
        });
      }
      return;
    }

    if (connectionMap.has(reverseKey)) {
      const existing = connectionMap.get(reverseKey);
      if (existing.type === 'unidirectional') {
        existing.type = 'bidirectional';
        existing.meta = item;
        connectionMap.set(reverseKey, existing);
      }
      return;
    }

    if (!connectionMap.has(forwardKey)) {
      connectionMap.set(forwardKey, {
        id: forwardKey,
        from: startKey,
        to: arriveKey,
        type: 'unidirectional',
        meta: item,
      });
    }
  });

  return {
    points: Array.from(pointMap.values()),
    connections: Array.from(connectionMap.values()),
  };
};

const extractCode = (value) => {
  if (!value) {
    return undefined;
  }
  if (typeof value === 'string') {
    return value.trim() || undefined;
  }
  if (typeof value === 'object') {
    return value.code || undefined;
  }
  return undefined;
};

const filterRecordsByParams = (records = [], params = {}) => {
  const departureCode = extractCode(params.departure);
  const arrivalCode = extractCode(params.arrival);

  if (!departureCode && !arrivalCode) {
    return records;
  }

  const matchesCode = (codes = [], targetCode) => {
    if (!targetCode) {
      return true;
    }
    return codes.filter(Boolean).some((code) => code === targetCode);
  };

  return records.filter((item) => {
    const departureCodes = [item.startSiteCode, item.startRegionCode, item.startProvinceCode];
    const arrivalCodes = [item.arriveSiteCode, item.arriveRegionCode, item.arriveProvinceCode];

    const departureMatched = matchesCode(departureCodes, departureCode);
    const arrivalMatched = matchesCode(arrivalCodes, arrivalCode);

    if (departureCode && arrivalCode) {
      return departureMatched && arrivalMatched;
    }
    if (departureCode) {
      return departureMatched;
    }
    if (arrivalCode) {
      return arrivalMatched;
    }
    return true;
  });
};

export default {
  namespace: 'connectivityAnalysis',
  state: {
    searchParams: {
      departure: undefined,
      arrival: undefined,
    },
    onSearch: false,
    tableDatas: [],
    rawTableDatas: [],
    points: [],
    connections: [],
  },

  effects: {
    /**
     * 获取数据
     */
    *selectRouteLinkAnalyse({ payload }, { call, put, select }) {
      const { searchParams, rawTableDatas } = yield select(
        ({ connectivityAnalysis }) => connectivityAnalysis,
      );
      const params = { ...searchParams, ...payload };

      yield put({
        type: 'updateState',
        payload: { onSearch: true },
      });

      const previousData = rawTableDatas;
      const hasParams =
        extractCode(params.departure) !== undefined || extractCode(params.arrival) !== undefined;

      try {
        let sourceData = previousData;

        if (!Array.isArray(sourceData) || !sourceData.length || !hasParams) {
          const response = yield call(selectRouteLinkAnalyse);
          if (response?.code === 1) {
            sourceData = Array.isArray(response?.data?.routeLinkAnalyseList)
              ? response.data.routeLinkAnalyseList
              : [];
          } else {
            response?.message && message.error(response.message);
            sourceData = [];
          }
        }

        const filteredData = filterRecordsByParams(sourceData, params);
        const fullMapData = buildMapData(sourceData);
        const filteredMapData = buildMapData(filteredData);

        yield put({
          type: 'updateState',
          payload: {
            rawTableDatas: sourceData,
            tableDatas: filteredData,
            points: fullMapData.points,
            connections: filteredMapData.connections,
          },
        });
      } catch (error) {
        console.error('selectRouteLinkAnalyse 调用失败:', error);
        message.error('联通情况数据获取失败');
        const fallbackData = Array.isArray(previousData)
          ? filterRecordsByParams(previousData, params)
          : [];
        const fallbackFullMap = buildMapData(previousData || []);
        const fallbackFilteredMap = buildMapData(fallbackData);
        yield put({
          type: 'updateState',
          payload: {
            rawTableDatas: previousData || [],
            tableDatas: fallbackData,
            points: fallbackFullMap.points,
            connections: fallbackFilteredMap.connections,
          },
        });
      } finally {
        yield put({
          type: 'updateState',
          payload: { onSearch: false },
        });
      }
    },
    *exportRouteLinkAnalyse(_, { call }) {
      try {
        const response = yield call(exportRouteLinkAnalyse);
        if (response?.code === 1) {
          message.success('请登录公司WPS企业账号至“我的云文档-我的导出”目录查看导出文件');
        } else {
          response?.message && message.error(response.message);
        }
      } catch (error) {
        console.error('selectRouteLinkAnalyse 调用失败:', error);
        message.error('下载联通明细表请求失败');
      }
    },
  },

  reducers: {
    updateState(state, { payload }) {
      return { ...state, ...payload };
    },
    updateSearchParams(state, { payload }) {
      return { ...state, searchParams: { ...state.searchParams, ...payload } };
    },
    resetState(state) {
      return { state, tableDatas: [], offset: undefined };
    },
  },
};
